import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { createPago } from "../services/pagosService";
import { listDeportistas } from "../services/deportistasService";
import { Select, Popover, Button, Input, Tooltip } from "antd";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";
import Modal from "antd/es/modal/Modal";
import {
  listProductos,
  createProducto,
  updateProducto,
  deleteProducto,
} from "../services/productosService";

const ProductoOption = ({ producto, onUpdate, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    nombre: producto.nombre,
    descripcion: producto.descripcion || "",
    monto: producto.monto || "",
  });

  const handleSave = async (e) => {
    e.stopPropagation();
    await onUpdate(producto.id, form);
    setOpen(false);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setOpen(false);
    onDelete(producto);
  };

  return (
    <div
      className="flex justify-between items-center w-full group"
      onMouseDown={(e) => e.preventDefault()}
    >
      <span>{producto.nombre}</span>
      <Popover
        open={open}
        onOpenChange={(visible) => {
          if (visible) {
            setForm({
              nombre: producto.nombre,
              descripcion: producto.descripcion || "",
              monto: producto.monto || "",
            });
          }
          setOpen(visible);
        }}
        content={
          <div
            className="flex flex-col gap-3 w-64"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Input
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="Nombre"
            />
            <Input.TextArea
              value={form.descripcion}
              onChange={(e) =>
                setForm({ ...form, descripcion: e.target.value })
              }
              placeholder="Descripción"
            />
            <Input
              type="number"
              value={form.monto}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || /^\d*\.?\d*$/.test(value)) {
                  setForm({ ...form, monto: value });
                }
              }}
              placeholder="Monto (USD)"
              min="0"
              step="0.01"
            />
            <div className="flex gap-2 justify-end">
              <Button danger size="small" onClick={handleDelete}>
                Eliminar
              </Button>
              <Button type="primary" size="small" onClick={handleSave}>
                Guardar
              </Button>
            </div>
          </div>
        }
        trigger="click"
        placement="right"
        zIndex={1060}
      >
        <EditOutlined
          className="text-slate-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      </Popover>
    </div>
  );
};

export function ModalCrearOrdenPago({
  open,
  onClose,
  atletas: atletasProp,
  repsById,
  onCreated,
}) {
  const [creating, setCreating] = useState(false);
  const [mode, setMode] = useState("single");
  const [selectedIds, setSelectedIds] = useState([]);

  const [productos, setProductos] = useState([]);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [editingProducto, setEditingProducto] = useState(null);
  const [productoForm, setProductoForm] = useState({
    nombre: "",
    descripcion: "",
    monto: "",
  });

  const [showDeleteProducto, setShowDeleteProducto] = useState(false);
  const [deletingProducto, setDeletingProducto] = useState(false);
  const [targetDeleteProducto, setTargetDeleteProducto] = useState(null);

  const [form, setForm] = useState({
    deportista_id: "",
    producto_id: "",
    producto: "",
    productoMonto: 0,
    periodo: "",
    fecha_vencimiento: "",
  });

  const [atletas, setAtletas] = useState(atletasProp || []);
  const [loadingAtletas, setLoadingAtletas] = useState(false);

  useEffect(() => {
    setAtletas(atletasProp || []);
  }, [atletasProp]);

  useEffect(() => {
    if (open) {
      loadProductos();
      loadDeportistas();
    }
  }, [open]);

  async function loadProductos() {
    try {
      setLoadingProductos(true);
      const data = await listProductos();
      setProductos(data);
    } catch (error) {
      console.error("Error cargando productos", error);
      toast.error("Error al cargar productos");
    } finally {
      setLoadingProductos(false);
    }
  }

  async function loadDeportistas() {
    try {
      setLoadingAtletas(true);
      const data = await listDeportistas();
      setAtletas(data || []);
    } catch (error) {
      console.error("Error cargando deportistas", error);
      toast.error("Error al cargar deportistas");
    } finally {
      setLoadingAtletas(false);
    }
  }

  useEffect(() => {
    if (open) {
      const now = new Date();
      const year = now.getFullYear();
      const nextYear = year + 1;
      const inTenDays = new Date(now.getTime() + 10 * 24 * 60 * 60 * 1000);

      setForm({
        deportista_id: "",
        producto_id: "",
        producto: "",
        productoMonto: 0,
        periodo: `${year} - ${nextYear}`,
        fecha_vencimiento: inTenDays.toISOString().slice(0, 10),
      });
      setMode("single");
      setSelectedIds([]);
    }
  }, [open]);

  function handleOpenPopover(productoToEdit = null) {
    if (productoToEdit) {
      setEditingProducto(productoToEdit);
      setProductoForm({
        nombre: productoToEdit.nombre,
        descripcion: productoToEdit.descripcion || "",
        monto: productoToEdit.monto || "",
      });
    } else {
      setEditingProducto(null);
      setProductoForm({ nombre: "", descripcion: "", monto: "" });
    }
    setPopoverOpen(true);
  }

  async function handleSaveProducto() {
    if (!productoForm.nombre.trim()) {
      toast.warning("El nombre del producto es obligatorio");
      return;
    }

    try {
      if (editingProducto) {
        await updateProducto(editingProducto.id, productoForm);
        toast.success("Producto actualizado correctamente");
      } else {
        await createProducto(productoForm);
        toast.success("Producto creado correctamente");
      }
      setPopoverOpen(false);
      loadProductos();
    } catch (error) {
      console.error(error);
      toast.error("Error al guardar el producto");
    }
  }

  async function handleUpdateProductoItem(id, formData) {
    try {
      await updateProducto(id, formData);
      toast.success("Producto actualizado correctamente");
      loadProductos();
      if (form.producto_id === id) {
        setForm((prev) => ({
          ...prev,
          producto: formData.nombre,
          productoMonto: formData.monto || 0,
        }));
      }
    } catch (error) {
      console.error(error);
      toast.error("Error al actualizar el producto");
    }
  }

  function handleDeleteProducto(producto) {
    setTargetDeleteProducto(producto);
    setShowDeleteProducto(true);
  }

  async function confirmDeleteProducto() {
    if (!targetDeleteProducto?.id) return;
    setDeletingProducto(true);
    try {
      await deleteProducto(targetDeleteProducto.id);
      toast.success("Producto eliminado");
      loadProductos();
      if (form.producto_id === targetDeleteProducto.id) {
        setForm((prev) => ({
          ...prev,
          producto_id: "",
          producto: "",
          productoMonto: 0,
        }));
      }
      setShowDeleteProducto(false);
      setTargetDeleteProducto(null);
    } catch (error) {
      console.error(error);
      toast.error("Error al eliminar el producto");
    } finally {
      setDeletingProducto(false);
    }
  }

  const deportistaOptions = useMemo(
    () =>
      (atletas || []).map((a) => ({
        label: `${a.nombres || ""} ${a.apellidos || ""}`.trim() || "Sin nombre",
        value: a.id,
        original: a,
      })),
    [atletas]
  );

  const filterDeportistaOption = (input, option) => {
    const label = (option?.label ?? "").toString().toLowerCase();
    const original = option?.data?.original || {};

    const categoria = original?.grupos?.categoria || "";
    const grupoNombre = original?.grupos?.nombre || "";
    const posicionNombre = original?.posiciones?.nombre || "";

    const extra = `${original.cedula || ""} ${
      original.email || ""
    } ${categoria} ${grupoNombre} ${posicionNombre}`.toLowerCase();
    const needle = input.toLowerCase();

    return label.includes(needle) || extra.includes(needle);
  };

  const selectedAtleta = useMemo(
    () =>
      mode === "single"
        ? (atletas || []).find((a) => a.id === form.deportista_id) || null
        : null,
    [atletas, form.deportista_id, mode]
  );

  const selectedRep =
    selectedAtleta && selectedAtleta.representante_id
      ? repsById[selectedAtleta.representante_id]
      : null;

  if (!open) return null;

  function handleToggleMode(nextMode) {
    setMode(nextMode);
    if (nextMode !== "single") {
      setForm((prev) => ({ ...prev, deportista_id: "" }));
    }
    if (nextMode !== "multi") {
      setSelectedIds([]);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!form.producto_id) {
      toast.error("El producto es obligatorio.");
      return;
    }

    if (!form.periodo.trim()) {
      toast.error("El periodo es obligatorio.");
      return;
    }

    if (!form.fecha_vencimiento) {
      toast.error("La fecha de vencimiento es obligatoria.");
      return;
    }

    const montoNumber = Number(form.productoMonto);

    const basePayloadFields = {
      producto_id: form.producto_id,
      monto: montoNumber,
      periodo: form.periodo.trim(),
      fecha_vencimiento: form.fecha_vencimiento,
      estado: "pending",
      tipo_pago: "por_definir",
      comprobante_url: null,
      confirmado_por: null,
      fecha_confirmacion: null,
    };

    if (mode === "single") {
      if (!form.deportista_id) {
        toast.error("Selecciona un deportista.");
        return;
      }

      const atleta = (atletas || []).find((a) => a.id === form.deportista_id);
      if (!atleta) {
        toast.error("No se encontró el deportista seleccionado.");
        return;
      }

      const representante_id = atleta.representante_id;
      if (!representante_id) {
        toast.error("El deportista no tiene representante asociado.");
        return;
      }

      const payload = {
        representante_id,
        deportista_id: form.deportista_id,
        ...basePayloadFields,
      };

      try {
        setCreating(true);
        await createPago(payload);
        toast.success("Orden de pago creada correctamente.");
        onClose();
        if (onCreated) {
          await onCreated();
        }
      } catch (err) {
        console.error(err);
        toast.error(err?.message || "Error creando orden de pago.");
      } finally {
        setCreating(false);
      }

      return;
    }

    if (mode === "multi") {
      const allAtletas = atletas || [];

      if (selectedIds.length === 0) {
        toast.error("Selecciona al menos un deportista.");
        return;
      }

      const seleccionados = allAtletas.filter((a) =>
        selectedIds.includes(a.id)
      );

      if (seleccionados.length === 0) {
        toast.error("No se encontraron los deportistas seleccionados.");
        return;
      }

      const conRep = seleccionados.filter((a) => !!a.representante_id);

      if (conRep.length === 0) {
        toast.error(
          "Ninguno de los deportistas seleccionados tiene representante asociado."
        );
        return;
      }

      try {
        setCreating(true);
        const results = await Promise.allSettled(
          conRep.map((a) => {
            const payload = {
              representante_id: a.representante_id,
              deportista_id: a.id,
              ...basePayloadFields,
            };
            return createPago(payload);
          })
        );

        const successCount = results.filter(
          (r) => r.status === "fulfilled"
        ).length;
        const failCount = results.length - successCount;

        if (successCount > 0) {
          toast.success(
            `Se crearon ${successCount} órdenes de pago para los deportistas seleccionados.`
          );
        }

        if (failCount > 0) {
          console.warn(
            "[PAGOS][CREATE_MODAL][MULTI] Fallos al crear órdenes:",
            failCount
          );
          toast.error(
            `No se pudo crear la orden para ${failCount} deportista(s). Revisa la consola para más detalle.`
          );
        }

        if (successCount > 0) {
          onClose();
          if (onCreated) {
            await onCreated();
          }
        }
      } catch (err) {
        console.error(err);
        toast.error(
          err?.message ||
            "Error creando órdenes de pago para los deportistas seleccionados."
        );
      } finally {
        setCreating(false);
      }

      return;
    }

    if (mode === "all") {
      const allAtletas = atletas || [];

      if (allAtletas.length === 0) {
        toast.error("No hay deportistas registrados para generar órdenes.");
        return;
      }

      const conRep = allAtletas.filter((a) => !!a.representante_id);

      if (conRep.length === 0) {
        toast.error(
          "Ningún deportista tiene representante asociado. No se puede generar órdenes."
        );
        return;
      }

      try {
        setCreating(true);
        const results = await Promise.allSettled(
          conRep.map((a) => {
            const payload = {
              representante_id: a.representante_id,
              deportista_id: a.id,
              ...basePayloadFields,
            };
            return createPago(payload);
          })
        );

        const successCount = results.filter(
          (r) => r.status === "fulfilled"
        ).length;
        const failCount = results.length - successCount;

        if (successCount > 0) {
          toast.success(
            `Se crearon ${successCount} órdenes de pago para deportistas registrados.`
          );
        }

        if (failCount > 0) {
          console.warn(
            "[PAGOS][CREATE_MODAL][BULK] Fallos al crear órdenes:",
            failCount
          );
          toast.error(
            `No se pudo crear la orden para ${failCount} deportista(s). Revisa la consola para más detalle.`
          );
        }

        if (successCount > 0) {
          onClose();
          if (onCreated) {
            await onCreated();
          }
        }
      } catch (err) {
        console.error(err);
        toast.error(
          err?.message || "Error creando órdenes de pago para todos."
        );
      } finally {
        setCreating(false);
      }
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-base font-semibold">Generar orden de pago</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="space-y-1">
              <span className="block text-xs font-medium text-slate-700">
                Tipo de generación
              </span>
              <div className="inline-flex rounded-full bg-slate-100 p-1 text-xs">
                <button
                  type="button"
                  onClick={() => handleToggleMode("single")}
                  className={`px-3 py-1 rounded-full font-medium transition ${
                    mode === "single"
                      ? "bg-white shadow text-slate-900"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  Un deportista
                </button>
                <button
                  type="button"
                  onClick={() => handleToggleMode("multi")}
                  className={`px-3 py-1 rounded-full font-medium transition ${
                    mode === "multi"
                      ? "bg-white shadow text-slate-900"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  Varios deportistas
                </button>
                <button
                  type="button"
                  onClick={() => handleToggleMode("all")}
                  className={`px-3 py-1 rounded-full font-medium transition ${
                    mode === "all"
                      ? "bg-white shadow text-slate-900"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  Todos los deportistas
                </button>
              </div>
              {mode === "all" && (
                <p className="text-[11px] text-slate-500 mt-1">
                  Se generará una orden de pago para{" "}
                  <span className="font-semibold">
                    todos los deportistas que tengan representante asociado
                  </span>{" "}
                  usando el mismo producto, monto, periodo y fecha de
                  vencimiento.
                </p>
              )}
              {mode === "multi" && (
                <p className="text-[11px] text-slate-500 mt-1">
                  Selecciona uno o varios deportistas. Se generará una orden por
                  cada uno con el mismo producto, monto, periodo y fecha de
                  vencimiento.
                </p>
              )}
            </div>

            {mode === "single" && (
              <div className="w-full bg-white [&_.ant-select-selector]:!bg-white [&_.ant-select-selector]:!border-slate-300">
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-slate-700">
                    Deportista
                  </label>
                  <Select
                    className="w-full"
                    placeholder="Busca y selecciona un deportista"
                    showSearch
                    loading={loadingAtletas}
                    optionFilterProp="label"
                    filterOption={filterDeportistaOption}
                    value={form.deportista_id || undefined}
                    onChange={(val) =>
                      setForm((prev) => ({
                        ...prev,
                        deportista_id: val,
                      }))
                    }
                    options={deportistaOptions}
                    optionRender={(option) => {
                      const a = option.data.original;
                      const fullName =
                        `${a.nombres || ""} ${a.apellidos || ""}`.trim() ||
                        "Sin nombre";

                      const categoria = a?.grupos?.categoria || "Sin categoría";

                      return (
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-800">
                            {fullName}
                          </span>
                          <span className="text-[11px] text-slate-700">
                            {a.cedula || "Sin cédula"} · {categoria}
                          </span>
                        </div>
                      );
                    }}
                  />
                  {selectedRep && (
                    <p className="text-[11px] text-slate-500">
                      Representante:{" "}
                      <span className="font-medium">
                        {`${selectedRep.nombres || ""} ${
                          selectedRep.apellidos || ""
                        }`.trim() || "Sin nombre"}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            )}

            {mode === "multi" && (
              <div className="w-full bg-white [&_.ant-select-selector]:!bg-white [&_.ant-select-selector]:!border-slate-300">
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-slate-700">
                    Deportistas seleccionados ({selectedIds.length})
                  </label>
                  <Select
                    mode="multiple"
                    className="w-full"
                    placeholder="Busca y selecciona uno o varios deportistas"
                    showSearch
                    loading={loadingAtletas}
                    optionFilterProp="label"
                    filterOption={filterDeportistaOption}
                    value={selectedIds}
                    onChange={(values) => setSelectedIds(values)}
                    options={deportistaOptions}
                    optionRender={(option) => {
                      const a = option.data.original;
                      const fullName =
                        `${a.nombres || ""} ${a.apellidos || ""}`.trim() ||
                        "Sin nombre";

                      const categoria = a?.grupos?.categoria || "Sin categoría";

                      return (
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-800">
                            {fullName}
                          </span>
                          <span className="text-[11px] text-slate-700">
                            {a.cedula || "Sin cédula"} · {categoria}
                          </span>
                        </div>
                      );
                    }}
                  />
                </div>
              </div>
            )}

            <div className="space-y-1">
              <label className="block text-xs font-medium text-slate-700">
                Producto
              </label>
              <div className="flex items-center gap-2">
                <Select
                  className="w-full"
                  placeholder="Selecciona un producto"
                  loading={loadingProductos}
                  value={form.producto_id || undefined}
                  onChange={(val) => {
                    const selected = productos.find((c) => c.id === val);
                    setForm((prev) => ({
                      ...prev,
                      producto_id: val,
                      producto: selected ? selected.nombre : "",
                      productoMonto: selected ? selected.monto || 0 : 0,
                    }));
                  }}
                  options={productos.map((c) => ({
                    label: c.nombre,
                    value: c.id,
                    original: c,
                  }))}
                  optionRender={(option) => (
                    <ProductoOption
                      producto={option.data.original}
                      onUpdate={handleUpdateProductoItem}
                      onDelete={handleDeleteProducto}
                    />
                  )}
                />

                <Popover
                  content={
                    <div className="flex flex-col gap-3 w-64">
                      <Input
                        placeholder="Nombre del producto"
                        value={productoForm.nombre}
                        onChange={(e) =>
                          setProductoForm({
                            ...productoForm,
                            nombre: e.target.value,
                          })
                        }
                      />
                      <Input.TextArea
                        placeholder="Descripción (opcional)"
                        value={productoForm.descripcion}
                        onChange={(e) =>
                          setProductoForm({
                            ...productoForm,
                            descripcion: e.target.value,
                          })
                        }
                      />
                      <Input
                        type="number"
                        placeholder="Monto (USD)"
                        value={productoForm.monto}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            setProductoForm({
                              ...productoForm,
                              monto: value,
                            });
                          }
                        }}
                        min="0"
                        step="0.01"
                      />
                      <Button type="primary" onClick={handleSaveProducto}>
                        Guardar
                      </Button>
                    </div>
                  }
                  title="Nuevo Producto"
                  trigger="click"
                  open={popoverOpen}
                  onOpenChange={(visible) => {
                    if (!visible) setPopoverOpen(false);
                  }}
                >
                  <Tooltip title="Crear nuevo producto">
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() => handleOpenPopover(null)}
                    />
                  </Tooltip>
                </Popover>
              </div>
            </div>

            {form.producto_id && (
              <div className="space-y-1">
                <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
                  <p className="text-xs text-blue-700">
                    <span className="font-semibold">Monto del producto:</span>{" "}
                    {new Intl.NumberFormat("es-EC", {
                      style: "currency",
                      currency: "USD",
                    }).format(form.productoMonto || 0)}
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-1">
              <label className="block text-xs font-medium text-slate-700">
                Periodo
              </label>
              <input
                type="text"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                value={form.periodo}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    periodo: e.target.value,
                  }))
                }
                placeholder="Ej: 2025 - 2026"
              />
            </div>

            <div className="space-y-1">
              <label className="block text-xs font-medium text-slate-700">
                Fecha de vencimiento
              </label>
              <input
                type="date"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                value={form.fecha_vencimiento}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    fecha_vencimiento: e.target.value,
                  }))
                }
              />
              <p className="text-[11px] text-slate-500">
                La orden se crea en estado{" "}
                <span className="font-semibold">pendiente</span> con el{" "}
                <span className="font-semibold">
                  monto del producto seleccionado
                </span>
                . Luego el representante enviará el comprobante según el tipo de
                pago.
              </p>
            </div>
          </div>

          <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
              disabled={creating}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={creating}
            >
              {creating
                ? mode === "all"
                  ? "Creando órdenes..."
                  : mode === "multi"
                  ? "Creando órdenes..."
                  : "Creando..."
                : mode === "all"
                ? "Crear órdenes para todos"
                : mode === "multi"
                ? "Crear órdenes para seleccionados"
                : "Crear orden"}
            </button>
          </div>
        </form>
      </div>

      <Modal
        open={showDeleteProducto}
        onCancel={() => {
          setShowDeleteProducto(false);
          setTargetDeleteProducto(null);
        }}
        title="Eliminar producto"
        footer={null}
        centered
        zIndex={1061}
      >
        <p className="text-sm text-slate-600 mb-4">
          ¿Seguro que deseas eliminar el producto{" "}
          <span className="font-medium text-slate-900">
            {targetDeleteProducto?.nombre}
          </span>
          ? Esta acción no se puede deshacer.
        </p>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setShowDeleteProducto(false);
              setTargetDeleteProducto(null);
            }}
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 transition"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={confirmDeleteProducto}
            disabled={deletingProducto}
            className="rounded-xl bg-rose-600 text-white px-4 py-2 text-sm hover:bg-rose-700 disabled:opacity-60 transition"
          >
            {deletingProducto ? "Eliminando..." : "Eliminar"}
          </button>
        </div>
      </Modal>
    </div>
  );
}
